import { InvalidResetPasswordCodeError } from "@/api/IdentityApi";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import { useIdentity } from "@/components/contexts/IdentityContext/UseIdentity";
import { LoadingDots } from "@/components/ui/LoadingDots";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/shadcn/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/shadcn/form";
import { FormInput } from "@/components/ui/shadcn/input";
import { Link } from "@/components/ui/shadcn/link";
import { Heading, Text } from "@/components/ui/Typography";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, KeyRound } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { animDelay } from "@/lib/utils";

const PasswordFormSchema = z
  .object({
    password: z
      .string()
      .nonempty("Please enter a password")
      .min(8, "Password must be at least 8 characters long")
      .max(100, "Password must be at most 100 characters long")
      .regex(/[0-9]/, "Password must contain at least one digit")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .refine(
        (password) => new Set(password.split("")).size >= 4,
        "Password must contain at least 4 unique characters",
      ),
    confirmPassword: z.string().nonempty("Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "The two passwords do not match",
    path: ["confirmPassword"],
  });

type PasswordFormData = z.infer<typeof PasswordFormSchema>;

function ResetPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const { resetPassword } = useIdentity();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const resetCode = searchParams.get("code");
  const navigate = useNavigate();

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(PasswordFormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = useCallback(
    async (data: PasswordFormData) => {
      setIsLoading(true);

      try {
        await resetPassword({
          email,
          resetCode,
          newPassword: data.password,
        });

        navigate("/reset-password-success");
      } catch (error) {
        if (error instanceof InvalidResetPasswordCodeError) {
          form.setError("root", {
            message: "Reset code is invalid or expired",
          });
        } else {
          form.setError("root", {
            message: "Something went wrong. Please try again later.",
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [form, navigate, resetPassword, email, resetCode],
  );

  if (!email || !resetCode) {
    return (
      <AuthLayout>
        <div className="anim-slide-up flex flex-col items-center text-center">
          <header className="flex flex-col items-center gap-4">
            <Logo variant="A" className="mb-4 w-[144px] sm:w-[156px]" />
            <div className="flex size-16 items-center justify-center rounded-2xl bg-destructive/10">
              <AlertCircle size={28} className="text-destructive" />
            </div>
            <Heading
              variant="h1"
              weight="medium"
              className="text-3xl sm:text-3xl"
            >
              Invalid Reset Link
            </Heading>
            <Text variant="text3" className="max-w-xs text-muted-foreground">
              This password reset link is invalid or has expired. Please request
              a new one to continue.
            </Text>
          </header>
          <div className="mt-6 w-full">
            <Link to="/request-password-reset">
              <Button className="w-full">Request New Link</Button>
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center">
        <header
          className="anim-slide-up flex flex-col items-center gap-4"
          style={animDelay(50)}
        >
          <Logo variant="A" className="mb-4 w-[144px] sm:w-[156px]" />
          <div
            className="anim-scale-in flex size-16 items-center justify-center rounded-2xl bg-primary/10"
            style={animDelay(150)}
          >
            <KeyRound size={28} className="text-primary" />
          </div>
          <Heading
            variant="h1"
            weight="medium"
            className="text-3xl sm:text-3xl"
          >
            Reset Password
          </Heading>
          <Text variant="text3" className="text-muted-foreground">
            Create a new password for your account.
          </Text>
        </header>

        <div className="anim-slide-up mt-6 w-full" style={animDelay(200)}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
              <div className="mx-auto flex w-full flex-col items-center text-left">
                <div className="flex w-full flex-col gap-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FormInput
                            type="password"
                            placeholder="Enter new password"
                            autoComplete="new-password"
                            disabled={isLoading}
                            required
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FormInput
                            type="password"
                            placeholder="Re-enter new password"
                            autoComplete="new-password"
                            disabled={isLoading}
                            required
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button
                  type="submit"
                  className="mt-6 w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <LoadingDots color="current" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                {form.formState.errors.root && (
                  <div className="anim-slide-down">
                    <Text variant="text3" className="mt-3 text-destructive">
                      {form.formState.errors.root.message}
                    </Text>
                  </div>
                )}
              </div>
            </form>
          </Form>
        </div>
      </div>
    </AuthLayout>
  );
}

export default ResetPasswordPage;
